import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { Feature } from '@/pinia/feature'
import MeasurementCard, { ColoredEntities, Value } from '../components/MeasurementCard.vue'
import { computed, toRefs } from 'vue'
import TableDataSource, { Table } from '../components/TableDataSource.vue'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { FeatureType } from '@/composables/useFilters'
import { useFetchFeature } from '@/composables/useFetchFeature'
import { calculateIndoorClimateKpiQuery } from '@/fetches'
import { ColorScaleConfig } from '@/utils/colorScale'
import { KpiQuery, KpiResponse, KpiType } from '@/api-client/models/indoorclimate/Contracts'
import { FormatOptions } from '../formatter'

type Props = {
  title: string
  info?: string
  type: KpiType
  feature: Feature
  initialShowOnMap?: boolean
  colorScale: ColorScaleConfig
  format?: FormatOptions
  detailRowNames: Record<KpiType, LocaleString>
  detailHeaders: {
    type: LocaleString
    value: LocaleString
  }
}

type IndexMeasurements = {
  value?: number
  coloredEntities?: {
    id: string
    meta: { numberOfSpaces: number }
    value: number
  }[]
  rows?: Record<KpiType, number | undefined>
}


export default /*@__PURE__*/_defineComponent({
  __name: 'IndexMeasurementCard',
  props: {
    title: {},
    info: { default: undefined },
    type: {},
    feature: {},
    initialShowOnMap: { type: Boolean, default: false },
    colorScale: {},
    format: { default: undefined },
    detailRowNames: {},
    detailHeaders: {}
  },
  setup(__props: any) {

const props = __props

const { data: measurements, isLoading } = toRefs(
  useFetchFeature<FeatureType.IndoorClimate, KpiQuery, KpiResponse, IndexMeasurements>(
    FeatureType.IndoorClimate,
    'indoor-climate-ici-measurements',
    '/iot/v1/indoorclimate/kpi',
    (filters) =>
      calculateIndoorClimateKpiQuery(
        filters,
        'ICI',
        'ICI_TEMPERATURE_COMFORT_COOL',
        'ICI_TEMPERATURE_COMFORT_WARM',
        'ICI_TEMPERATURE_STABILITY',
        'ICI_CO2'
      ) ?? undefined,
    ({ kpis }): IndexMeasurements | undefined => {
      const valueMeasurement = kpis.find((m) => m.type === props.type)
      if (!valueMeasurement) return undefined
      const { values: valueSplits } = valueMeasurement

      const coloredEntitiesMeasurement = kpis.find((m) => m.type === props.type && m.splitBy)
      return {
        value: valueSplits?.[0]?.value !== null ? valueSplits?.[0]?.value : undefined,
        coloredEntities: coloredEntitiesMeasurement?.values
          ?.map((s) => ({
            id: s.splitKey,
            meta: { numberOfSpaces: s.numberOf?.['SPACE'] },
            value: s.value
          }))
          .filter((s) => s.value !== null) as IndexMeasurements['coloredEntities'],
        rows: kpis.reduce((acc, m) => {
          if (m.type && m.type !== props.type) {
            acc[m.type] = m.values?.[0]?.value ?? undefined
          }
          return acc
        }, {} as Record<KpiType, number | undefined>)
      }
    }
  )
)

const value = computed<Value | undefined>((): Value | undefined => ({
  type: 'gauge',
  value: measurements.value?.value,
  isLoading: isLoading.value,
  format: props.format
}))

const coloredEntities = computed<ColoredEntities>(
  (): ColoredEntities => ({
    colorings: measurements.value?.coloredEntities,
    enabledEntityLevels: ['COMPLEX', 'FLOOR', 'CONSTRUCTION', 'LEVEL']
  })
)

const ICI_SUB_TYPES: KpiType[] = [
  'ICI_TEMPERATURE_COMFORT_COOL',
  'ICI_TEMPERATURE_COMFORT_WARM',
  'ICI_TEMPERATURE_STABILITY',
  'ICI_CO2'
]

const table = computed<Table>((): Table => {
  return {
    headers: [props.detailHeaders.type, props.detailHeaders.value],
    rows: ICI_SUB_TYPES.map((key) => [
      {
        type: 'text',
        text: props.detailRowNames[key]
      },
      {
        type: 'number',
        number: measurements.value?.rows?.[key],
        isLoading: isLoading.value,
        format: props.format
      }
    ])
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(MeasurementCard, {
    title: _ctx.title,
    info: _ctx.info,
    initialShowOnMap: _ctx.initialShowOnMap,
    feature: _ctx.feature,
    value: value.value,
    coloredEntities: coloredEntities.value,
    colorScale: _ctx.colorScale,
    table: table.value
  }, {
    details: _withCtx(() => [
      _createVNode(TableDataSource, { table: table.value }, null, 8, ["table"])
    ]),
    _: 1
  }, 8, ["title", "info", "initialShowOnMap", "feature", "value", "coloredEntities", "colorScale", "table"]))
}
}

})